import { useState, useEffect, useCallback } from 'react';

const CHAT_HISTORY_API = process.env.REACT_APP_CHAT_HISTORY_API || '';

export const useChatHistory = (userId, agentType) => {
  const [chatSessions, setChatSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load chat sessions for the user and agent type
  const loadChatSessions = useCallback(async () => {
    if (!userId || !agentType || !CHAT_HISTORY_API) {
      console.log('❌ Missing required params for loading chat history');
      console.log('userId:', userId);
      console.log('agentType:', agentType);
      console.log('CHAT_HISTORY_API:', CHAT_HISTORY_API);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const url = `${CHAT_HISTORY_API}?userId=${encodeURIComponent(userId)}&agentType=${encodeURIComponent(agentType)}&limit=50`;
      console.log('📡 Loading chat sessions from:', url);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to load chat sessions: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Loaded chat sessions:', data);
      console.log('Number of sessions:', data.sessions?.length || 0);

      setChatSessions(data.sessions || []);
    } catch (err) {
      console.error('❌ Error loading chat sessions:', err);
      setError(err.message);
      setChatSessions([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId, agentType]);

  // Save or update a chat session
  const saveChatSession = useCallback(async (sessionId, messages, title = '', role = '') => {
    if (!userId || !agentType || !sessionId || !CHAT_HISTORY_API) {
      console.log('❌ Missing required params for saving chat');
      console.log('userId:', userId);
      console.log('agentType:', agentType);
      console.log('sessionId:', sessionId);
      console.log('CHAT_HISTORY_API:', CHAT_HISTORY_API);
      return;
    }

    try {
      console.log('💾 Saving chat session:', sessionId);
      console.log('Messages count:', messages?.length);
      console.log('Title:', title);
      console.log('Role:', role);

      const payload = {
        userId,
        sessionId,
        agentType,
        messages,
        title: title || (messages[0]?.text?.substring(0, 50) + '...' || 'Untitled Chat'),
        role: role  // Add role to payload
      };
      
      console.log('Payload:', JSON.stringify(payload, null, 2));

      const response = await fetch(CHAT_HISTORY_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to save chat session: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ Chat session saved:', data);

      // Reload sessions to update the list
      await loadChatSessions();

      return data;
    } catch (err) {
      console.error('❌ Error saving chat session:', err);
      setError(err.message);
      throw err;
    }
  }, [userId, agentType, loadChatSessions]);

  // Load a specific chat session
  const loadChatSession = useCallback(async (sessionId) => {
    if (!sessionId || !CHAT_HISTORY_API) {
      return null;
    }

    try {
      console.log('Loading chat session:', sessionId);

      const url = `${CHAT_HISTORY_API}/${sessionId}?userId=${encodeURIComponent(userId)}&agentType=${encodeURIComponent(agentType)}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to load chat session: ${response.status}`);
      }

      const data = await response.json();
      console.log('Loaded chat session:', data);

      return data;
    } catch (err) {
      console.error('Error loading chat session:', err);
      setError(err.message);
      return null;
    }
  }, [userId, agentType]);

  // Delete a chat session
  const deleteChatSession = useCallback(async (sessionId) => {
    if (!sessionId || !userId || !agentType || !CHAT_HISTORY_API) {
      return;
    }

    try {
      console.log('Deleting chat session:', sessionId);

      const response = await fetch(`${CHAT_HISTORY_API}/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          userId,
          agentType 
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete chat session: ${response.status}`);
      }

      console.log('Chat session deleted');

      // Reload sessions to update the list
      await loadChatSessions();
    } catch (err) {
      console.error('Error deleting chat session:', err);
      setError(err.message);
      throw err;
    }
  }, [userId, agentType, loadChatSessions]);

  // Load sessions on mount
  useEffect(() => {
    loadChatSessions();
  }, [loadChatSessions]);

  return {
    chatSessions,
    isLoading,
    error,
    saveChatSession,
    loadChatSession,
    deleteChatSession,
    refreshSessions: loadChatSessions,
  };
};
